import { Badge, getPaymentStatusBadge } from '@/components/ui/Badge';
import { API_BASE_URL } from '@/lib/api';
import { formatCurrency, formatDate, formatSeatIdentifier } from '@/lib/utils';
import type { PurchaseWithTickets } from '@eventflow/shared-types';
import { AlertTriangle, FileText, Flame, GraduationCap, Hourglass, Ticket, DollarSign } from 'lucide-react';
import { ProofViewer } from './ProofViewer';

interface PaymentDetailPanelProps {
  purchase: PurchaseWithTickets | null;
  onApprove: () => void;
  onReject: () => void;
  isProcessing: boolean;
  deadlineInfo?: {
    label: string;
    rule: string;
    isExpired: boolean;
    isExpiringSoon: boolean;
    hoursRemaining: number;
  } | null;
}

export function PaymentDetailPanel({
  purchase,
  onApprove,
  onReject,
  isProcessing,
  deadlineInfo,
}: PaymentDetailPanelProps) {
  // Estado vacío
  if (!purchase) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-gray-400 p-8">
        <div className="w-24 h-24 bg-gray-100 rounded-2xl flex items-center justify-center mb-6">
          <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-600 mb-2">Selecciona un pago</h3>
        <p className="text-sm text-center max-w-xs">
          Haz clic en un pago de la lista para ver sus detalles y realizar acciones
        </p>
        <div className="mt-6 flex items-center gap-2 text-xs text-gray-400">
          <kbd className="px-2 py-1 bg-gray-100 rounded border border-gray-200 font-mono">↑</kbd>
          <kbd className="px-2 py-1 bg-gray-100 rounded border border-gray-200 font-mono">↓</kbd>
          <span>para navegar</span>
        </div>
      </div>
    );
  }

  const hasProof = Boolean(purchase.payment_proof_url);
  const statusBadge = getPaymentStatusBadge(purchase.payment_status, hasProof);
  const isPending = purchase.payment_status === 'pending';
  const isConfirmed = purchase.payment_status === 'confirmed';

  return (
    <div className="h-full flex flex-col">
      {/* Header con info principal */}
      <div className="p-6 border-b border-gray-200 bg-white">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-1">{purchase.customer_name}</h2>
            <p className="text-sm text-gray-500">{purchase.customer_email}</p>
            {purchase.customer_phone && (
              <p className="text-sm text-gray-500">{purchase.customer_phone}</p>
            )}
          </div>
          <Badge variant={statusBadge.variant} size="md">
            {statusBadge.icon} {statusBadge.label}
          </Badge>
        </div>

        {/* Info del evento */}
        <div className="mt-4 flex flex-wrap gap-4 text-sm">
          <div className="flex items-center gap-2 text-gray-600">
            <span className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600">
              <Ticket className="w-5 h-5" />
            </span>
            <div>
              <p className="font-medium text-gray-900">{purchase.event.name}</p>
              <p className="text-xs text-gray-500">{formatDate(purchase.event.event_date)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Contenido scrollable */}
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {/* Deadline warning (solo para pendientes) */}
        {isPending && deadlineInfo && (
          <div
            className={`
            rounded-xl p-4 border
            ${deadlineInfo.isExpired
                ? 'bg-red-50 border-red-200'
                : deadlineInfo.isExpiringSoon
                  ? 'bg-amber-50 border-amber-200'
                  : 'bg-blue-50 border-blue-200'
              }
          `}
          >
            <div className="flex items-start gap-3">
              <span className="text-2xl">
                {deadlineInfo.isExpired ? (
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                ) : deadlineInfo.isExpiringSoon ? (
                  <Flame className="w-5 h-5 text-amber-600" />
                ) : (
                  <Hourglass className="w-5 h-5 text-blue-600" />
                )}
              </span>
              <div>
                <p
                  className={`font-semibold ${deadlineInfo.isExpired
                    ? 'text-red-800'
                    : deadlineInfo.isExpiringSoon
                      ? 'text-amber-800'
                      : 'text-blue-800'
                    }`}
                >
                  {deadlineInfo.isExpired
                    ? 'Tiempo agotado'
                    : `Tiempo restante: ${deadlineInfo.label}`}
                </p>
                {deadlineInfo.rule && (
                  <p
                    className={`text-sm mt-1 ${deadlineInfo.isExpired
                      ? 'text-red-600'
                      : deadlineInfo.isExpiringSoon
                        ? 'text-amber-600'
                        : 'text-blue-600'
                      }`}
                  >
                    {deadlineInfo.rule}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Visor de comprobante */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <span className="w-6 h-6 bg-gray-100 rounded flex items-center justify-center">
              <FileText className="w-4 h-4" />
            </span>
            Comprobante de Pago
          </h3>
          <ProofViewer proofUrl={purchase.payment_proof_url} purchaseId={purchase.id} />
        </div>

        {/* Datos de conciliación */}
        <div className="bg-gray-50 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <span className="w-6 h-6 bg-gray-200 rounded flex items-center justify-center">
              <DollarSign className="w-4 h-4" />
            </span>
            Datos de Conciliación
          </h3>

          <div className="grid grid-cols-2 gap-4">
            {/* Monto esperado */}
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Monto Esperado</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(purchase.total_amount)}
              </p>
            </div>

            {/* Asientos */}
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Asientos</p>
              <p className="text-2xl font-bold text-gray-900">{purchase.tickets.length}</p>
              <div className="mt-2 flex flex-wrap gap-1">
                {purchase.tickets.slice(0, 3).map((ticket) => (
                  <span
                    key={ticket.id}
                    className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded"
                  >
                    {formatSeatIdentifier(ticket.seat.seat_identifier)}
                  </span>
                ))}
                {purchase.tickets.length > 3 && (
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                    +{purchase.tickets.length - 3} más
                  </span>
                )}
              </div>
            </div>

            {/* Referencia bancaria */}
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Referencia</p>
              <p className="font-mono text-lg font-bold text-gray-900">
                {purchase.bank_reference || purchase.id.substring(0, 8).toUpperCase()}
              </p>
            </div>

            {/* Fecha de compra */}
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Fecha de Compra</p>
              <p className="text-sm font-medium text-gray-900">{formatDate(purchase.created_at)}</p>
            </div>
          </div>

          {/* Tipo de cliente (egresado UNACH) */}
          {purchase.customer_type === 'egresado_unach' && purchase.graduate_program && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
              <p className="text-xs font-medium text-blue-600 mb-1 inline-flex items-center gap-2">
                <GraduationCap className="w-4 h-4" />
                Egresado UNACH
              </p>
              <p className="text-sm text-blue-800">{purchase.graduate_program}</p>
            </div>
          )}

          {/* Notas */}
          {purchase.notes && (
            <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-100">
              <p className="text-xs font-medium text-amber-600 mb-1 inline-flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Notas
              </p>
              <p className="text-sm text-amber-800">{purchase.notes}</p>
            </div>
          )}
        </div>

        {/* Link de descarga de tickets (solo si confirmado) */}
        {isConfirmed && (
          <div className="bg-emerald-50 rounded-xl p-5 border border-emerald-200">
            <h3 className="text-sm font-semibold text-emerald-700 mb-3 flex items-center gap-2">
              <span className="w-6 h-6 bg-emerald-200 rounded flex items-center justify-center text-sm">
                <Ticket className="w-4 h-4" />
              </span>
              Tickets Generados
            </h3>
            <a
              href={`${API_BASE_URL}/purchases/${purchase.id}/tickets`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              Descargar Tickets PDF
            </a>
          </div>
        )}
      </div>

      {/* Footer con acciones - Sticky */}
      {isPending && (
        <div className="p-6 border-t border-gray-200 bg-white shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onReject}
              disabled={isProcessing}
              className="flex-1 py-2.5 px-4 text-sm font-medium rounded-lg bg-red-500 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isProcessing ? (
                <svg className="animate-spin h-4 w-4 mx-auto" fill="none" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
              ) : (
                'Rechazar'
              )}
            </button>
            <button
              type="button"
              onClick={onApprove}
              disabled={isProcessing || !hasProof}
              title={!hasProof ? 'El cliente debe subir un comprobante primero' : 'Aprobar pago'}
              className="flex-1 py-2.5 px-4 text-sm font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isProcessing ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Procesando...
                </span>
              ) : (
                'Aprobar Pago'
              )}
            </button>
          </div>

          {!hasProof && (
            <p className="mt-3 text-xs text-center text-amber-600 flex items-center justify-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              No puedes aprobar sin comprobante. El cliente debe subirlo primero.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
